import { getMainSections, getSectionUiKey } from '@/lib/page-utils';
import { getDocument } from '@/lib/utils';
import { PAGE_EDITOR_HISTORY_TARGET_IDS } from '@/stores/page-editor/constants';
import { usePageEditorStore } from '@/stores/page-editor-store';

export const PAGE_EDITOR_SCROLL_CONTAINER_SELECTOR = '[data-page-editor-scroll-container]';
export const PAGE_EDITOR_SECTION_ID_ATTR = 'data-page-editor-section-id';

const PAGE_EDITOR_SECTION_SCROLL_OFFSET_PX = 24;

const SCROLL_TO_SECTION_MAX_FRAMES = 5;
// A cross-page undo/redo navigates and re-hydrates the target page before the
// scroll runs, so the target element (e.g. a footer) can mount well after the
// first few frames. Retry over a longer window so the scroll still lands.
const SCROLL_TO_SECTION_MOUNT_MAX_FRAMES = 60;
const SCROLL_TO_SECTION_LAYOUT_STABLE_EPSILON_PX = 0.5;
const SCROLL_TO_SECTION_LAYOUT_STABLE_FRAMES = 2;

const SLOT_LAYOUT_STABILITY_TARGET_IDS = new Set<string>([
  PAGE_EDITOR_HISTORY_TARGET_IDS.header,
  PAGE_EDITOR_HISTORY_TARGET_IDS.footer,
]);

function readPageEditorSnapshot(): {
  page: ReturnType<typeof usePageEditorStore.getState>['page'] | null;
  incomingSectionIdMappings: Record<string, string>;
} {
  const { page, incomingSectionIdMappings } = usePageEditorStore.getState();

  return {
    page: page ?? null,
    incomingSectionIdMappings: incomingSectionIdMappings ?? {},
  };
}

function findPageEditorScrollContainer(): HTMLElement | null {
  return getDocument()?.querySelector<HTMLElement>(PAGE_EDITOR_SCROLL_CONTAINER_SELECTOR) ?? null;
}

function collectSectionDomIds(sectionId: string): string[] {
  const ids = new Set<string>([sectionId]);
  const { page, incomingSectionIdMappings } = readPageEditorSnapshot();
  const remappedId = incomingSectionIdMappings[sectionId];
  if (remappedId) {
    ids.add(remappedId);
  }

  for (const section of getMainSections(page)) {
    if (section.id === sectionId || section.uiKey === sectionId || section.id === remappedId) {
      ids.add(getSectionUiKey(section));
    }
  }

  return [...ids];
}

export function findPageEditorSectionElement(sectionId: string): HTMLElement | null {
  const doc = getDocument();
  if (!doc || !sectionId) {
    return null;
  }

  for (const candidateId of collectSectionDomIds(sectionId)) {
    const element = doc.querySelector<HTMLElement>(
      `[${PAGE_EDITOR_SECTION_ID_ATTR}="${candidateId}"]`,
    );
    if (element) {
      return element;
    }
  }

  return null;
}

export function scrollPageEditorToSection(sectionId: string): boolean {
  const sectionElement = findPageEditorSectionElement(sectionId);
  if (!sectionElement) {
    return false;
  }

  const scrollContainer = findPageEditorScrollContainer();
  if (scrollContainer) {
    const containerRect = scrollContainer.getBoundingClientRect();
    const sectionRect = sectionElement.getBoundingClientRect();
    const targetTop =
      sectionRect.top -
      containerRect.top +
      scrollContainer.scrollTop -
      PAGE_EDITOR_SECTION_SCROLL_OFFSET_PX;

    scrollContainer.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    });
    return true;
  }

  sectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return true;
}

export function scheduleScrollToPageEditorSection(sectionId: string): void {
  if (!sectionId) {
    return;
  }

  let framesRemaining = SCROLL_TO_SECTION_MOUNT_MAX_FRAMES;
  let lastObservedTop: number | null = null;
  let consecutiveStableFrames = 0;

  const attemptScroll = () => {
    const sectionElement = findPageEditorSectionElement(sectionId);
    if (sectionElement) {
      const shouldWaitForStableLayout = SLOT_LAYOUT_STABILITY_TARGET_IDS.has(sectionId);
      if (!shouldWaitForStableLayout) {
        scrollPageEditorToSection(sectionId);
        return;
      }

      const currentTop = sectionElement.getBoundingClientRect().top;
      if (
        lastObservedTop !== null &&
        Math.abs(currentTop - lastObservedTop) <= SCROLL_TO_SECTION_LAYOUT_STABLE_EPSILON_PX
      ) {
        consecutiveStableFrames += 1;
      } else {
        consecutiveStableFrames = 1;
      }
      lastObservedTop = currentTop;

      if (
        consecutiveStableFrames >= SCROLL_TO_SECTION_LAYOUT_STABLE_FRAMES ||
        framesRemaining <= 1
      ) {
        scrollPageEditorToSection(sectionId);
        return;
      }
    }

    framesRemaining -= 1;
    if (framesRemaining > 0) {
      requestAnimationFrame(attemptScroll);
    }
  };

  requestAnimationFrame(attemptScroll);
}

export function reorderSectionWithViewportRetain(sectionId: string, reorder: () => void): void {
  const scrollContainer = findPageEditorScrollContainer();
  const sectionElement = findPageEditorSectionElement(sectionId);
  if (!scrollContainer || !sectionElement) {
    reorder();
    return;
  }

  const anchorTop =
    sectionElement.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top;

  reorder();

  let attempts = 0;

  const attemptRetain = () => {
    attempts += 1;

    const element = findPageEditorSectionElement(sectionId);
    if (!element) {
      if (attempts < SCROLL_TO_SECTION_MAX_FRAMES) {
        requestAnimationFrame(attemptRetain);
      }
      return;
    }

    const drift =
      element.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top - anchorTop;
    if (Math.abs(drift) > 0.5) {
      scrollContainer.scrollTop += drift;
    }

    // Keep checking for a few frames so we converge even if layout/React updates land asynchronously.
    if (attempts < SCROLL_TO_SECTION_MAX_FRAMES) {
      requestAnimationFrame(attemptRetain);
    }
  };

  requestAnimationFrame(attemptRetain);
}
